import { useCallback, useRef } from 'react';

import type {
  StepFlowInstance,
  StepFlowStepComponent,
  StepFlowStepStatus,
} from './types';

export function useStepFlow<T extends string | number | symbol = string>() {
  const stepFlowRef = useRef<StepFlowInstance<T>>(null);

  const setStatus = useCallback(
    (status: StepFlowStepStatus<T>, ...args: any[]) => {
      stepFlowRef.current?.setStatus(status, ...args);
    },
    [],
  );

  const reset = useCallback(() => {
    stepFlowRef.current?.reset();
  }, []);

  const proceed = useCallback((...args: any[]) => {
    stepFlowRef.current?.proceed(...args);
  }, []);

  const drawback = useCallback((...args: any[]) => {
    stepFlowRef.current?.drawback(...args);
  }, []);

  const getStepsValue = useCallback(() => {
    return stepFlowRef.current?.getStepsValue() ?? {};
  }, []);

  const getStepValue = useCallback((key: StepFlowStepStatus<T>) => {
    return stepFlowRef.current?.getStepValue(key);
  }, []);

  const getStatus = useCallback(() => {
    return stepFlowRef.current?.getStatus();
  }, []);

  const createStep = useCallback(
    <TProps = Record<string, unknown>>(
      step: StepFlowStepComponent<TProps, T>,
    ): StepFlowStepComponent<TProps, T> => {
      return step;
    },
    [],
  );

  return {
    stepFlowRef,
    createStep,
    setStatus,
    reset,
    proceed,
    drawback,
    getStepsValue,
    getStepValue,
    getStatus,
  };
}
